import React, { useEffect, useState } from "react";
import { User, Palette, Boxes, Plug } from "lucide-react";
import api from "@/lib/api";
import { useT, useI18n } from "@/i18n";
import { useTheme } from "@/context/ThemeProvider";
import { useAuth } from "@/context/AuthProvider";

function Panel({ icon: Icon, title, hint, children, testid }) {
  return (
    <section className="rounded-lg border border-border bg-card p-6" data-testid={testid}>
      <h3 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wide text-muted-foreground">
        <Icon className="h-4 w-4 text-brand" />{title}
      </h3>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

const STATUS_TONE = {
  active: "bg-success/10 text-success",
  live: "bg-success/10 text-success",
  scaffolded: "bg-warning/10 text-warning",
  planned: "bg-secondary text-muted-foreground",
  beta: "bg-brand/10 text-brand",
};

export default function Settings() {
  const t = useT();
  const { lang, setLang } = useI18n();
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();
  const [arch, setArch] = useState(null);

  useEffect(() => {
    api.get("/architecture").then(({ data }) => setArch(data)).catch(() => {});
  }, []);

  const statusLabel = (s) => t(`common.${s}`) !== `common.${s}` ? t(`common.${s}`) : s;

  return (
    <div className="max-w-3xl">
      <h1 className="font-display text-3xl font-extrabold tracking-tighter">{t("settings.title")}</h1>
      <p className="mt-1 text-sm text-muted-foreground">{t("settings.subtitle")}</p>

      <div className="mt-8 space-y-6">
        <Panel icon={User} title={t("settings.account")} testid="settings-account">
          <div className="grid gap-4 sm:grid-cols-2">
            <div><p className="text-xs text-muted-foreground">{t("settings.name")}</p><p className="mt-0.5 font-medium">{user?.name}</p></div>
            <div><p className="text-xs text-muted-foreground">{t("settings.email")}</p><p className="mt-0.5 font-medium">{user?.email}</p></div>
            <div><p className="text-xs text-muted-foreground">{t("settings.authProvider")}</p><p className="mt-0.5 font-mono text-sm capitalize">{user?.auth_provider}</p></div>
          </div>
        </Panel>

        <Panel icon={Palette} title={t("settings.appearance")} testid="settings-appearance">
          <div className="flex flex-wrap gap-8">
            <div>
              <p className="mb-2 text-xs text-muted-foreground">{t("settings.themeMode")}</p>
              <div className="inline-flex rounded-md border border-border p-1">
                {["light", "dark"].map((m) => (
                  <button key={m} onClick={() => setTheme(m)} data-testid={`theme-${m}`}
                    className={`rounded px-3 py-1.5 text-sm font-medium transition-colors ${theme === m ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                    {t(`theme.${m}`)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-2 text-xs text-muted-foreground">{t("settings.language")}</p>
              <div className="inline-flex rounded-md border border-border p-1">
                {["en", "it"].map((l) => (
                  <button key={l} onClick={() => setLang(l)} data-testid={`setting-lang-${l}`}
                    className={`rounded px-3 py-1.5 text-sm font-medium transition-colors ${lang === l ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                    {t(`language.${l}`)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Panel>

        <Panel icon={Boxes} title={t("settings.architecture")} hint={t("settings.architectureHint")} testid="settings-architecture">
          <div className="grid gap-2 sm:grid-cols-2">
            {arch?.engines?.map((e) => (
              <div key={e.key} className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
                <span className="text-sm font-medium">{e.label}</span>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_TONE[e.status] || STATUS_TONE.planned}`}>{statusLabel(e.status)}</span>
              </div>
            ))}
          </div>
        </Panel>

        <Panel icon={Plug} title={t("settings.connectors")} hint={t("settings.connectorsHint")} testid="settings-connectors">
          <div className="grid gap-2 sm:grid-cols-2">
            {arch?.connectors?.map((c) => (
              <div key={c.key} className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
                <div>
                  <p className="text-sm font-medium">{c.label}</p>
                  <p className="font-mono text-[11px] text-muted-foreground">{c.protocol}</p>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_TONE[c.status] || STATUS_TONE.planned}`}>{statusLabel(c.status)}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
